import React, { useState, useCallback, useRef, useEffect, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { centerPoints, mapOptions } from '../../config-map'
import Map from '../Map/Map'
import TripsChart from '../TripsChart'
import Trips from './Trips'
import Vehicles from './Vehicles'
import './fleet-tracking.scss'
import TripsContext from '../../contexts/TripsContext'

import GoogleAucomplete from '../GoogleAutocomplete/GoogleAucomplete'
import LayersMenu from '../../shared/LayersMenu/LayersMenu'
import ShapesMap from '../ShapesMap/ShapesMap'
import { usePois } from '../../hooks/usePois'

import { PropsFactory } from '../../factory/PropsFactory'

import PolylineTrips from '../PolylineTrips'
import { filterHardwareAND, filterHardwareData } from '../../utils'
import { InfoWindow } from '@react-google-maps/api'
import ButtonFullScreen from '../ButtonFullScreen'
import MarkerTrip from '../MarkerTrip'
import MarkerVehicle2 from '../MarkerVehicle2'
import ViewSettings from '../ViewSettings'
import MarkerPoi from '../MarkerPoi'
import { filtersAND } from './types'

const mapOptionsCustom = {
    ...mapOptions,
    streetViewControl: true,
    fullscreen: false,
    styles: [
        {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }]
        }
    ]
}

const build = (
    vehicles = [],
    vehiclesSearched,
    filterActive,
    hardwareData = {},
    filtersAND = [],
    filtersANDActive = {},
    setFilteredImeis,
    setAndApplied
) => {
    let result = vehiclesSearched || vehicles
    let hw = filterHardwareData(result, hardwareData)

    if (Object.keys(filtersANDActive).length > 0) {
        hw = filterHardwareAND(filtersAND, filtersANDActive, hw)
        result = result.filter(({ imei }) => hw[imei])
        setFilteredImeis(() => result.map(({ imei }) => imei))
        setAndApplied(true)
    }

    if (filterActive && filterActive.length > 0) {
        result = filterByStatus(result, hardwareData, filterActive)
    }

    return result
}

const filterByStatus = (vehicles, hardwareData, status) => {
    const hwFiltered = {}
    Object.keys(hardwareData).forEach(key => {
        const hw = hardwareData[key]
        if (status.includes(hw.car_status)) hwFiltered[key] = hw
    })

    return vehicles.filter(({ imei }) => {
        const hw = hwFiltered[imei] || {}

        return status.includes(hw.car_status)
    })
}

const FleetTracking = ({
    zoom = 18,
    perspectives,
    restrictedPerspectives,
    btnFiltersOR = [],
    filtersAND = [],
    maxSpeed,
    isLoading
}: any): JSX.Element => {
    const {
        trips,
        isNavegateToTrips,
        centerMap,
        hardwareData = {},
        onLoadMap,
        mapRef,
        vehicles = [],
        filterActive,
        vehiclesSearched,
        mapInstance,
        infoWindow,
        viewSettings,
        selectedVehicles,
        markerHashMap,
        setInfoWindow,
        setVehicleActive,
        setCenterMap
    } = useContext(TripsContext)

    const [pointsInterest, filteredIds] = usePois(perspectives, restrictedPerspectives, viewSettings)

    //const pointsInterest
    // const { TripsPill } = useFiltersPills(btnFiltersOR)

    const [filterActiveAND, setFilterActiveAND] = useState<any>()
    const [isFullScreen, setIsFullScreen] = useState(false)
    const [loadedCenterFirstVehicle, setLoadedCenterFirstVehicle] = useState(false)
    const [filteredImeis, setFilteredImeis] = useState([])
    const [andApplied, setAndApplied] = useState(false)

    const vehiclesComputed = useMemo(() => {
        return build(
            vehicles,
            vehiclesSearched,
            filterActive,
            hardwareData,
            filtersAND,
            filterActiveAND,
            setFilteredImeis,
            setAndApplied
        )
    }, [vehicles, filterActive, vehiclesSearched, hardwareData, filterActiveAND, filtersAND, isNavegateToTrips])

    useEffect(() => {
        if (navigator?.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
                setCenterMap({ lat: position.coords.latitude, lng: position.coords.longitude })
            })
        }
    }, [])

    useEffect(() => {
        centerPoints(mapInstance, centerMap)
    }, [centerMap])

    useEffect(() => {
        const hasSomeData = Object.keys(hardwareData).length > 0
        if (hasSomeData && !loadedCenterFirstVehicle) {
            const { lat, lng } = hardwareData[Object.keys(hardwareData)[0]]
            setLoadedCenterFirstVehicle(true)
            setCenterMap({ lat, lng })
        }
    }, [hardwareData])

    useEffect(() => {
        if (mapInstance) {
            google.maps.event.addListener(mapInstance, 'click', event => {
                setInfoWindow(undefined)
                setVehicleActive(undefined)
                selectedVehicles.forEach((ele: any) => {
                    const marker = markerHashMap.get(ele.imei)
                    marker?.marker.setMap(null)
                })
                selectedVehicles.length = 0
            })
        }
    }, [mapInstance])

    const applyFilters = useCallback(
        filter => {
            if (Object.keys(filter).length == 0) setAndApplied(false)
            setFilterActiveAND(filter)
        },
        [setFilterActiveAND]
    )

    return (
        <StyledFleetTracking>
            {!isFullScreen && (
                <StyledLeftContainer>
                    <Vehicles
                        isLoading={isLoading}
                        filters={filtersAND}
                        applyFilters={applyFilters}
                        data={vehiclesComputed}
                    />
                    <Trips
                        show={isNavegateToTrips}
                        vehicle={isNavegateToTrips?.vehicle}
                        data={hardwareData[isNavegateToTrips?.vehicle?.imei]}
                    />
                </StyledLeftContainer>
            )}

            {true && (
                <StyledRightContainer>
                    {isNavegateToTrips && !isFullScreen && (
                        <TripsChart uniqueID={isNavegateToTrips?.vehicle?.imei} collection={hardwareData?.[isNavegateToTrips?.vehicle?.imei].collection} />
                    )}

                    <Map
                        zoom={zoom}
                        center={centerMap}
                        onLoad={onLoadMap}
                        options={mapOptionsCustom}
                        mapContainerClassName="map-markers-container"
                    >
                        <StyledTopContainerActions>
                            {/* {viewSettings.allPois &&
                                <ShapesMap data={pointsInterest} />} */}

                            <div className="flex-1">
                                <GoogleAucomplete />
                            </div>
                            <div>
                                <LayersMenu mapRef={mapRef} />

                                <ButtonFullScreen callback={() => setIsFullScreen(!isFullScreen)} />
                            </div>
                        </StyledTopContainerActions>

                        {/*<StyledBootomContainerActions>*/}
                        {/*    <ViewSettings />*/}
                        {/*</StyledBootomContainerActions>*/}

                        {infoWindow && (
                            <InfoWindow
                                anchor={infoWindow?.marker}
                                position={infoWindow.latLng}
                                onCloseClick={() => setInfoWindow(undefined)}
                            >
                                {infoWindow.content}
                            </InfoWindow>
                        )}

                        {/* <PolylineItinerarie />  */}
                        {/* {TripsPill} */}
                        <PolylineTrips />
                        <MarkerVehicle2
                            vehicles={vehiclesComputed}
                            hardwareData={hardwareData}
                            filtersANDApplied={andApplied}
                            filteredImeis={filteredImeis}
                        />
                        {/*<MarkerPoi pois={pointsInterest} filteredIds={filteredIds} />*/}
                        <MarkerTrip trips={trips} />
                    </Map>
                </StyledRightContainer>
            )}
        </StyledFleetTracking>
    )
}

export default React.memo(FleetTracking)

const StyledFleetTracking = styled.div`
    display: flex;
    background: ${({ theme }) => theme['gray-300']};
    height: 100%;
    position: relative;
`

const StyledTopContainerActions = styled.div`
    display: flex;
    align-items: center;
    padding-left: 10px;
`

const StyledBootomContainerActions = styled.div`
    display: flex;
    align-items: center;
    padding-left: 10px;
    position: absolute;
    bottom: 12px;
    z-index: 1;
`

const StyledLeftContainer = styled.div`
    width: 552px;
    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme['gray-400']};
    position: relative;
`

const StyledRightContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-left: 8px;
    width: calc(100% - 562px);
`
